import { useState } from 'react';
import { confirmMonth, resetLocationHistory, ConfirmMonthRequest } from '../api/adminApi';
import styles from './ResetLocationPanel.module.css';

interface Props {
  locationCode: string;
  locationName: string;
  /** e.g. "Sha'ban 1447" — the most recent confirmed month, shown so the admin knows what gets deleted. */
  deletingMonthLabel: string;
  /** e.g. "Safar 1448" — the month that will be confirmed immediately after the reset. */
  targetLabel: string;
  /** Args for the post-reset confirmMonth call, or null while required fields (e.g. start date) are unfilled. */
  confirmArgs: Omit<ConfirmMonthRequest, 'locationCode'> | null;
  onSuccess: () => void;
}

/**
 * Destructive "wipe history and start clean" action, shared between InitializeModal
 * (reached when a country has never been confirmed at all) and AdjustDateModal
 * (reached when a country has stale/gapped history — the more common real-world path,
 * since the dashboard routes already-inconsistent countries to "Fix Issue" instead).
 */
export default function ResetLocationPanel({
  locationCode,
  locationName,
  deletingMonthLabel,
  targetLabel,
  confirmArgs,
  onSuccess,
}: Props) {
  const [resetMode, setResetMode] = useState(false);
  const [resetAck, setResetAck] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [error, setError] = useState('');

  async function handleReset() {
    if (!confirmArgs) return;
    setResetting(true);
    setError('');
    try {
      await resetLocationHistory(locationCode);
      await confirmMonth({ locationCode, ...confirmArgs });
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setResetting(false);
    }
  }

  return (
    <section className={styles.dangerZone}>
      {!resetMode ? (
        <button className={styles.dangerToggle} type="button" onClick={() => setResetMode(true)}>
          Start fresh from {targetLabel} instead →
        </button>
      ) : (
        <div className={styles.dangerBody}>
          <p className={styles.dangerHint}>
            This permanently deletes every previously confirmed and pending month on record for {locationName}{' '}
            (including {deletingMonthLabel}), then initializes directly from {targetLabel}. This cannot be undone.
          </p>
          <label className={styles.dangerCheckboxRow}>
            <input type="checkbox" checked={resetAck} onChange={e => setResetAck(e.target.checked)} />
            I understand this permanently deletes all previously confirmed months for {locationName}.
          </label>
          {error && <div className={styles.error}>{error}</div>}
          <div className={styles.actions}>
            <button
              className={styles.cancelBtn}
              type="button"
              onClick={() => { setResetMode(false); setResetAck(false); }}
              disabled={resetting}
            >
              Cancel
            </button>
            <button
              className={styles.dangerConfirmBtn}
              onClick={handleReset}
              disabled={!resetAck || !confirmArgs || resetting}
            >
              {resetting ? 'Resetting…' : 'Reset & Initialize'}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
